import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { fixMediaPath, generateTestId } from 'app/functions';
import { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import HotMaterialDialog from '../HotMaterialDialog';

const HtmlContentContainer = styled('div')(({ theme }) => `
    * {
        margin: 0;
        padding: 0;
    } 

    p {
        margin: 5px 0;
    }
`);

// ReactParser common options, to be used across the app
export function reactParserCommonOptions(options = {
    whitelistedTags: [],
    openLinksInNewTab: false,
}) {
    return {
        decodeEntities: true,
        transform: (node) => {
            // If "whitelistedTags" was defined, remove all other tags
            if (
                node?.type === 'tag' &&
                options?.whitelistedTags?.length > 0 &&
                !options?.whitelistedTags?.includes(node?.name)
            ) {
                return null;
            }

            // If "openLinksInNewTab" was defined, add target="_blank" to all links
            if (node?.name === 'a' && options?.openLinksInNewTab) {
                node.attribs.target = '_blank';
            }

            // Remove p tags without content (only space)
            if (node?.name === 'p' && /^\s+$/.test(node?.children?.[0]?.data)) {
                return null;
            }

            // Allow embedded content to be opened in full-screen
            if (node?.name === 'iframe') {
                node.attribs.allowfullscreen = true;
            }

            // In case of images with relative paths, pass it through CDN server
            if (node?.name === 'img') {
                node.attribs.src = fixMediaPath(node.attribs.src);
            }
        },
    };
}

const HtmlContent = forwardRef(({ 
    content,
    testId='',
    className='',
    onLinkStatusChange = (status) => {} // Optional callback prop with default empty function
}, ref) => {
    const containerRef = useRef(null);
    const [clickedLinks, setClickedLinks] = useState(new Set());
    const [allLinks, setAllLinks] = useState(new Set());
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogUrl, setDialogUrl] = useState('');
    // Reset tracking when content changes and collect all links
    useEffect(() => {
        setClickedLinks(new Set());
        
        // Collect all links from content once
        const tempDoc = new DOMParser().parseFromString(content, 'text/html');
        const links = tempDoc.getElementsByTagName('a');
        const newLinks = new Set();
        Array.from(links).forEach(link => {
            if (link.href) {
                newLinks.add(link.href);
            }
        });
        setAllLinks(newLinks);
        
        // Report initial status
        reportStatus(new Set(), newLinks);
    }, [content]);

    const reportStatus = (clicked = clickedLinks, all = allLinks) => {
        const status = {
            clickedLinks: Array.from(clicked),
            allLinks: Array.from(all),
            totalLinks: all.size,
            clickedCount: clicked.size,
            isComplete: Array.from(all).every(link => clicked.has(link))
        };
        onLinkStatusChange(status);
        return status;
    };

    useEffect(() => {
        // Function to handle individual link clicks
        const handleLinkClick = (e) => {
            e.preventDefault();
            const href = e.currentTarget.href;
            if (href) {
                setClickedLinks(prev => {
                    const newClickedLinks = new Set(prev);
                    newClickedLinks.add(href);
                    reportStatus(newClickedLinks, allLinks);
                    return newClickedLinks;
                });
                if (href.includes('/api/')){
                    setDialogOpen(true);
                    setDialogUrl(href);
                }else{
                    window.open(href, '_blank');
                }
            }
        };

        // Add click listeners to all anchor tags in the container
        if (containerRef.current) {
            const anchors = containerRef.current.getElementsByTagName('a');
            Array.from(anchors).forEach(anchor => {
                anchor.addEventListener('click', handleLinkClick);
            });
        }

        // Cleanup function to remove all listeners
        return () => {
            if (containerRef.current) {
                const anchors = containerRef.current.getElementsByTagName('a');
                Array.from(anchors).forEach(anchor => {
                    anchor.removeEventListener('click', handleLinkClick);
                });
            }
        };
    }, [content, allLinks]); // Re-run when content or allLinks changes

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        checkLinkStatus: () => reportStatus(),
        resetLinkStatus: () => {
            setClickedLinks(new Set());
            return reportStatus(new Set(), allLinks);
        }
    }), [allLinks, onLinkStatusChange]);

    return (
        <>
            <HtmlContentContainer className={className} ref={containerRef} testId={generateTestId(testId, 'html-content')} onClick={(e) => e.stopPropagation()}>
                {ReactHtmlParser(content, reactParserCommonOptions())}
            </HtmlContentContainer>
            <HotMaterialDialog open={dialogOpen} onClose={() => setDialogOpen(false)}  maxWidth="md">
                <iframe src={dialogUrl} style={{ width: '100%', height: '80vh', border: 'none' }} referrerPolicy="strict-origin-when-cross-origin" />
            </HotMaterialDialog>
        </>
    );
});

export default HtmlContent;